import React, { FC, useCallback, useContext } from 'react';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import Typography from '@mui/material/Typography';
import CloudDownload from '@mui/icons-material/CloudDownload';
import { useDownload } from '../../hooks/useDownload';
import { LicenseContext } from '../../contexts/LicenseContext';
import { useApiErrorHandler } from '../../hooks/useApiErrorHandler';
import { useTranslation } from 'react-i18next';
import { LicensesIncludedInOffline } from './LicensesIncludedInOffline';
import { ELicenseMode } from '../../models/ILicense';
import { LazyUser } from '../LazyData/LazyUser';
import { apiOrganization } from '../../resources/api/apiOrganization';
import { OrganizationContext } from '../../contexts/OrganizationContext';

interface IProps {
  onClose: () => void;
}

const { useListOrganizationUsers } = apiOrganization;

export const DownloadOfflineLicense: FC<IProps> = ({ onClose }) => {
  const license = useContext(LicenseContext)!;
  const download = useDownload();
  const apiErrorHandler = useApiErrorHandler();
  const { t } = useTranslation();
  const { organization } = useContext(OrganizationContext);
  const { Guard: GuardUsers } = useListOrganizationUsers(organization?.name);

  const onDownload = useCallback(
    async (e: React.MouseEvent) => {
      e.stopPropagation();
      if (!license || license.mode !== ELicenseMode.Airgap || !license.offlineLicensePath) return;
      try {
        await download(`${license.offlineLicensePath}.txt`);
      } catch (e) {
        apiErrorHandler(e, t('licenses.offlineDialog.downloadFailed'));
      }
    },
    [license, download]
  );

  return (
    <>
      <DialogTitle>{t('licenses.offlineDialog.title')}</DialogTitle>
      <DialogContent dividers>
        <Typography paragraph>{t('licenses.offlineDialog.description')}</Typography>
        <Box p={1} textAlign="center">
          <img src="/offline-step3.svg" />
        </Box>
        <Typography paragraph>{t('licenses.offlineDialog.content.ready')}</Typography>
        <GuardUsers>
          <Typography paragraph>
            {t('licenses.offlineDialog.userLabel')}: <LazyUser id={license.userUuid!} />
          </Typography>
        </GuardUsers>
        <Typography paragraph>
          {t('licenses.offlineDialog.machineLabel')}: <strong>{license.machineName}</strong>
        </Typography>

        <LicensesIncludedInOffline />
      </DialogContent>
      <DialogActions>
        <Button variant="text" onClick={onClose}>
          {t('common.dialog.close')}
        </Button>
        <Button variant="contained" color="primary" onClick={onDownload} startIcon={<CloudDownload />}>
          {t('licenses.offlineDialog.confirmDownload')}
        </Button>
      </DialogActions>
    </>
  );
};
